import { Link, useLocation } from 'react-router-dom';
import { SECTIONS } from '../../devmap';
import './NotFound.css';

/**
 * A signed-in developer at an address the portal does not have.
 *
 * The catch-all used to send everyone to the landing page, so a mistyped /apps
 * looked exactly like being signed out ("Sign in with ofSelf"). Inside the frame
 * it says what happened, and offers Home and the nearest real page.
 */

// Every first segment a page can start with, and where it lands. The section map
// carries the pages; the rest are routes that sit outside it.
const DOORS = [
  ...SECTIONS.flatMap((s) => s.pages.map((p) => ({ label: p.label, to: p.to }))),
  { label: 'Home', to: '/home' },
  { label: 'API docs', to: '/api-docs' },
  { label: 'Money', to: '/billing' },
];

// Words people type for a page that are not its address.
const ALIASES = {
  apps: '/applications', app: '/applications', application: '/applications',
  docs: '/documentation', doc: '/documentation', payouts: '/billing', money: '/billing',
  earnings: '/billing', calls: '/applications/metrics', metrics: '/applications/metrics',
  evals: '/applications/os-evals', token: '/tokens', new: '/create?lane=new', settings: '/home',
};

function distance(a, b) {
  const d = Array.from({ length: a.length + 1 }, (_, i) => [i, ...Array(b.length).fill(0)]);
  for (let j = 1; j <= b.length; j += 1) d[0][j] = j;
  for (let i = 1; i <= a.length; i += 1) {
    for (let j = 1; j <= b.length; j += 1) {
      d[i][j] = Math.min(d[i - 1][j] + 1, d[i][j - 1] + 1, d[i - 1][j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1));
    }
  }
  return d[a.length][b.length];
}

const pathOf = (to) => to.split('?')[0];
const segOf = (to) => pathOf(to).split('/')[1] || '';

/** The nearest real page to a path that is not one, or null. Pure; exported for tests. */
export function closestPage(pathname) {
  const seg = (pathname.split('/')[1] || '').toLowerCase();
  if (!seg) return null;
  const door = (to) => DOORS.find((d) => d.to === to) || DOORS.find((d) => pathOf(d.to) === pathOf(to));
  if (ALIASES[seg]) return door(ALIASES[seg]) || null;

  // A prefix either way (/schema → /schemas, /application → /applications), then
  // the fewest edits, if they are few enough to be a typo.
  const tops = DOORS.filter((d) => pathOf(d.to).split('/').length === 2);
  const prefix = tops.find((d) => segOf(d.to).startsWith(seg) || seg.startsWith(segOf(d.to)));
  if (prefix && seg.length >= 3) return prefix;
  let best = null;
  let bestD = Infinity;
  tops.forEach((d) => {
    const n = distance(seg, segOf(d.to));
    if (n < bestD) { best = d; bestD = n; }
  });
  return bestD <= Math.max(1, Math.floor(seg.length / 3)) ? best : null;
}

export default function NotFound() {
  const { pathname } = useLocation();
  const near = closestPage(pathname);
  return (
    <section className="dev-404">
      <p className="dev-404__code">404</p>
      <h1 className="dev-404__title">No page at <code>{pathname}</code></h1>
      <p className="dev-404__lede">The address may be mistyped, or the page has moved.</p>
      <div className="dev-404__acts">
        {near && near.to !== '/home' && (
          <Link className="dev-404__go dev-404__go--primary" to={near.to}>
            Go to {near.label} <span className="dev-404__path">{pathOf(near.to)}</span>
          </Link>
        )}
        <Link className="dev-404__go" to="/home">Home</Link>
      </div>
    </section>
  );
}
